import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNotificationCenter } from "hooks/useNotificationCenter";
import { useNotificationSocket } from "hooks/useNotificationSocket";
import { NotificationModel } from "services/notification.service";
import { getNotificationNavigationTarget } from "utils/notification";

const DROPDOWN_PAGE_SIZE = 10;

export const useNotificationDropdown = () => {
  const navigate = useNavigate();
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const {
    notificationList,
    unreadCount,
    hasMoreNotifications,
    isFetchingNotifications,
    isMarkingAllAsRead,
    refreshNotifications,
    scheduleRefreshNotifications,
    markNotificationAsReadById,
    markAllNotificationsAsRead,
    loadMoreNotifications,
  } = useNotificationCenter({
    pageSize: DROPDOWN_PAGE_SIZE,
    autoLoad: false,
  });

  useNotificationSocket(
    useCallback(() => {
      scheduleRefreshNotifications();
    }, [scheduleRefreshNotifications]),
  );

  useEffect(() => {
    if (isNotificationOpen) {
      void refreshNotifications();
    }
  }, [isNotificationOpen, refreshNotifications]);

  const handleOpenNotifications = useCallback((): void => {
    setIsNotificationOpen(false);
    navigate("/notifications");
  }, [navigate]);

  const handleClickNotification = useCallback(
    async (item: NotificationModel): Promise<void> => {
      const target = getNotificationNavigationTarget(item);

      try {
        if (item.status === "unread") {
          await markNotificationAsReadById(item.id);
        }
      } catch (error) {
        console.error("Open notification failed:", error);
      } finally {
        setIsNotificationOpen(false);
        navigate(target.pathname, {
          state: target.state,
        });
      }
    },
    [markNotificationAsReadById, navigate],
  );

  const handleLoadMoreNotifications = useCallback(async (): Promise<void> => {
    await loadMoreNotifications();
  }, [loadMoreNotifications]);

  const handleMarkAllAsRead = useCallback(async (): Promise<void> => {
    try {
      await markAllNotificationsAsRead();
    } catch (error) {
      console.error("Mark all notifications as read failed:", error);
    }
  }, [markAllNotificationsAsRead]);

  return {
    isNotificationOpen,
    setIsNotificationOpen,
    notificationList,
    unreadCount,
    hasMoreNotifications,
    isFetchingNotifications,
    isMarkingAllAsRead,
    handleOpenNotifications,
    handleClickNotification,
    handleLoadMoreNotifications,
    handleMarkAllAsRead,
  };
};
